import React, {
  useEffect,
  useRef,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import Select from 'core/components/Select';
import head from 'lodash/head';
import Dropzone from 'pages/Dashboard/components/Dropzone';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import clsx from 'clsx';
import {
  blobToDataUrl,
  notEmptyString,
} from '@populateui/utils';
import {
  FileRejection,
} from 'react-dropzone';
import isEmpty from 'lodash/isEmpty';
import {
  PdfTemplateBuilderLocationState,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  useAddForm,
  useGetFormCategories,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';
import isNil from 'lodash/isNil';
import {
  FormCategoryDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  LocalMediaFile,
} from 'pages/Dashboard/pages/Billing/types';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Spinner from 'pages/Dashboard/components/Spinner';

type FormCreateData = {
  title: string;
  category: FormCategoryDTO | null;
  document: LocalMediaFile | null;
};

type OpenBuilderParams = {
  formId: number;
  builderState: PdfTemplateBuilderLocationState;
};

export type CreateDialogProps = {
  category?: FormCategory | null;
  onClose: () => void;
  onOpenBuilder: (params: OpenBuilderParams) => void;
};

function getTitleFromFileName(fileName: string): string {
  return fileName.replace(/\.pdf$/i, '');
}

export default function CreateDialog({
  category,
  onClose,
  onOpenBuilder,
}: CreateDialogProps) {
  const isTitleManuallyEditedRef = useRef(false);
  const {
    data: formCategories = [],
    isLoading: isFormCategoriesLoading,
  } = useGetFormCategories();
  const formProps = useForm<FormCreateData>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      category: null,
      document: null,
    },
  });
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = formProps;

  const document = watch('document');
  const notify = useNotify();
  const {
    mutateAsync: addForm,
    isPending: isUploading,
  } = useAddForm();

  useEffect(() => {
    const matchedCategory = formCategories.find(
      (formCategoryOption) => formCategoryOption.value === category,
    );

    setValue(
      'category',
      !isNil(matchedCategory)
        ? matchedCategory
        : head(formCategories) ?? null,
      { shouldValidate: true },
    );
  }, [category, formCategories, setValue]);

  const revokeDocumentUrl = (mediaFile: LocalMediaFile | null | undefined) => {
    const url = mediaFile?.media?.url?.value;
    if (!isNil(url) && notEmptyString(url)) {
      URL.revokeObjectURL(url);
    }
  };

  const setDocumentFromFile = (file: File) => {
    revokeDocumentUrl(getValues('document'));
    setValue('document', {
      original: file,
      media: {
        url: { value: URL.createObjectURL(file) },
        fileName: file.name,
      },
    }, { shouldValidate: true, shouldDirty: true });

    if (!isTitleManuallyEditedRef.current) {
      setValue('title', getTitleFromFileName(file.name), { shouldValidate: true });
    }
  };

  const handleClose = () => {
    revokeDocumentUrl(getValues('document'));
    isTitleManuallyEditedRef.current = false;
    reset();
    onClose();
  };

  const handleDrop = (files: File[], rejections: FileRejection[]) => {
    if (!isEmpty(rejections)) {
      notify({
        variant: 'error',
        message: 'File type must be PDF',
      });
    } else {
      const file = head(files);
      if (!isNil(file)) {
        setDocumentFromFile(file);
      }
    }
  };

  const handleDocumentUpdate = (urls: FileLocalUrl[], file?: File) => {
    const nextFile = file ?? head(urls)?.file;
    if (!isNil(nextFile)) {
      if (nextFile.type !== 'application/pdf') {
        notify({
          variant: 'error',
          message: 'File type must be PDF',
        });
      } else {
        setDocumentFromFile(nextFile);
      }
    }
  };

  const onContinue = async (data: FormCreateData) => {
    const file = data.document?.original;
    if (!isNil(file)) {
      try {
        const nextTitle = data.title.trim();
        const dataUri = await blobToDataUrl(file);
        const createdForm = await addForm({
          dataUri,
          title: nextTitle,
          formCategory: data.category?.value as FormCategory,
        });
        const formId = createdForm.formId!;

        if (isNil(formId)) {
          throw new Error('Missing form id');
        }

        const objectUrl = data.document?.media?.url?.value
          ?? URL.createObjectURL(file);
        const builderState: PdfTemplateBuilderLocationState = {
          document: {
            fileName: file.name,
            url: objectUrl,
            isObjectUrl: true,
          },
          template: {
            id: String(formId),
            title: createdForm.title ?? nextTitle,
          },
        };

        reset({
          title: '',
          category: null,
          document: null,
        });
        onClose();
        onOpenBuilder({
          formId,
          builderState,
        });
      } catch {
        console.error('Error uploading consent form');
      }
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title="Create Form"
        onClose={handleClose}
        onConfirm={noop}
        open
      >
        <Controller
          name="title"
          control={control}
          rules={{
            validate: (value) => notEmptyString(value) || 'Title is required',
          }}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, isTouched } }) => (
            <Input
              {...field}
              value={typeof value === 'string' ? value : ''}
              onChange={(nextValue) => {
                isTitleManuallyEditedRef.current = true;
                onChange(nextValue);
              }}
              error={isTouched && invalid}
              className="text-gray-500 text-xs"
              id="title"
              placeholder="Enter Form Title"
              label="Title"
              required
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
        <Controller
          name="category"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field: { value, ...field } }) => (
            <Select<FormCategoryDTO>
              {...field}
              value={value ?? undefined}
              id="category"
              label="Category"
              options={formCategories}
              getValue={(option) => option.value}
              getLabel={(option) => option.name}
              className="text-gray-500 text-xs"
              disabled={isFormCategoriesLoading || formCategories.length === 0}
              required
            />
          )}
        />
        <Controller
          name="document"
          control={control}
          rules={{
            validate: (value) => !isNil(value) || 'Form document is required',
          }}
          render={() => (
            <div className="w-full max-h-[120px] overflow-y-hidden">
              {!isNil(document) ? (
                <MediaPreview
                  media={document.media}
                  onDelete={() => {
                    revokeDocumentUrl(document);
                    setValue('document', null, { shouldValidate: true });
                  }}
                  onUpdate={handleDocumentUpdate}
                  mediaType={MultimediaType.ConsentForms}
                  className="w-full"
                  thumbnail
                  hasDelete
                />
              ) : (
                <Dropzone
                  accept={{ 'application/pdf': ['.pdf'] }}
                  onDrop={handleDrop}
                  disabled={isUploading || isFormCategoriesLoading}
                  classes={{
                    root: clsx(
                      'flex flex-col w-full p-8 justify-center items-center gap-3',
                      'border border-dashed border-blue-500 bg-blue-50 rounded',
                      'hover:bg-blue-200 cursor-pointer transition-colors',
                    ),
                    text: 'text-blue-500 text-sm font-semibold',
                  }}
                  placeholder="Upload Form Document"
                />
              )}
            </div>
          )}
        />
        <div className="flex gap-2 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isUploading}
          >
            Close
          </Button>
          <DisabledFormTooltip>
            <Button
              variant="contained"
              color="primary"
              type="button"
              disabled={!isValid || isUploading || isFormCategoriesLoading}
              onClick={handleSubmit(onContinue)}
              className="flex gap-2 items-center"
            >
              {isUploading && <Spinner />}
              Continue
            </Button>
          </DisabledFormTooltip>
        </div>
      </Dialog>
    </FormProvider>
  );
}
